import * as Sentry from "@sentry/react";
import type betterAuthClient from "@/lib/__shared__/better-auth/client";
import type {
	BetterAuthSession,
	BetterAuthStore,
} from "./stores/betterAuthStore";

type BetterAuthClient = typeof betterAuthClient;

type SendOtpType = "sign-in";

export interface IBetterAuthManager {
	initialize(): Promise<void>;
	sendLoginOtp(_email: string): Promise<void>;
	verifyLoginOtp(_email: string, _otp: string): Promise<void>;
	signOut(): Promise<void>;
}

const messageFromError = (error: unknown, fallback: string): string => {
	if (error && typeof error === "object" && "message" in error) {
		const message = (error as { message?: unknown }).message;
		if (typeof message === "string" && message.length > 0) {
			return message;
		}
	}
	if (error instanceof Error) {
		return error.message;
	}
	return fallback;
};

export class BetterAuthManager implements IBetterAuthManager {
	private client: BetterAuthClient;
	private store: BetterAuthStore;

	constructor(client: BetterAuthClient, store: BetterAuthStore) {
		this.client = client;
		this.store = store;
	}

	async initialize(): Promise<void> {
		const set = this.store.getState();
		set.setAuthLoading(true);

		try {
			const { data, error } = await this.client.getSession();

			if (error) {
				Sentry.captureException(error);
				set.setSession(null);
				return;
			}

			set.setSession((data as BetterAuthSession | null) ?? null);
		} catch (err) {
			Sentry.captureException(err);
			set.setSession(null);
		} finally {
			set.setAuthLoading(false);
		}
	}

	async sendLoginOtp(email: string): Promise<void> {
		const set = this.store.getState();
		set.setAuthLoading(true);

		try {
			const { error } = await this.client.emailOtp.sendVerificationOtp({
				email,
				type: "sign-in" satisfies SendOtpType,
			});

			if (error) {
				throw new Error(
					messageFromError(error, "Failed to send verification code."),
				);
			}
		} finally {
			set.setAuthLoading(false);
		}
	}

	async verifyLoginOtp(email: string, otp: string): Promise<void> {
		const set = this.store.getState();
		set.setAuthLoading(true);

		try {
			const { error } = await this.client.signIn.emailOtp({ email, otp });

			if (error) {
				throw new Error(
					messageFromError(error, "Invalid or expired verification code."),
				);
			}

			const { data, error: sessionError } = await this.client.getSession();

			if (sessionError) {
				throw new Error(
					messageFromError(
						sessionError,
						"Signed in, but failed to load session.",
					),
				);
			}

			set.setSession((data as BetterAuthSession | null) ?? null);
		} finally {
			set.setAuthLoading(false);
		}
	}

	async signOut(): Promise<void> {
		const set = this.store.getState();
		set.setAuthLoading(true);

		try {
			await this.client.signOut();
		} catch (err) {
			Sentry.captureException(err);
		} finally {
			set.setSession(null);
			set.setAuthLoading(false);
		}
	}
}
